/**
 * Compensation REST API Client
 *
 * REST API client for Compensation Management functionality.
 * Replaces httpsCallable pattern with REST API calls to consolidated Express router.
 *
 * This file provides the same interface as the previous callable functions
 * for easy migration in domain hooks.
 */

import { apiRequest } from '../../lib/api/baseApiClient';
import type { CompensationProfile, UpdateCompensationPayload, PayrollSummary } from './types';
import type { CheckDisclaimerStatusResponse } from '@/lib/types/compensation';

const FUNCTION_NAME = 'compensationApi';

// =============================================================================
// Types
// =============================================================================

export interface BonusPayment {
  id: string;
  tenantId: string;
  employeeId: string;
  employeeName?: string;
  type: 'commission' | 'performance' | 'referral' | 'tip' | 'spot' | 'discretionary' | 'holiday' | 'retention';
  amount: number;
  currency: string;
  commissionPlanId?: string;
  tipPoolId?: string;
  calculatedAmount?: number;
  overrideAmount?: number;
  isOverridden: boolean;
  overrideReason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid' | 'reversed';
  submittedBy: string;
  submittedAt: string;
  submissionNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  payPeriod: { month: number; year: number };
  paidAt?: string;
  disputed: boolean;
  disputeReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBonusPaymentInput {
  employeeId: string;
  type: BonusPayment['type'];
  amount: number;
  currency?: string;
  notes?: string;
  payPeriod?: {
    month: number;
    year: number;
  };
}

export interface CommissionPlan {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  type: 'flat_rate' | 'tiered' | 'percentage' | 'custom';
  flatAmount?: number;
  percentage?: number;
  tiers?: Array<{
    min: number;
    max: number | null;
    rate: number;
    calculationType: 'percentage' | 'fixed_amount';
  }>;
  payoutFrequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  clawbackEnabled: boolean;
  clawbackPeriodDays?: number;
  status: 'active' | 'inactive' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommissionPlanInput {
  tenantId: string;
  name: string;
  description?: string;
  type: CommissionPlan['type'];
  flatAmount?: number;
  percentage?: number;
  tiers?: CommissionPlan['tiers'];
  payoutFrequency: CommissionPlan['payoutFrequency'];
  applicableRoles?: string[];
  applicableDepartments?: string[];
  applicableEmployeeIds?: string[];
  clawbackEnabled?: boolean;
  clawbackPeriodDays?: number;
}

export interface TipPool {
  id: string;
  tenantId: string;
  date: string;
  shiftId?: string;
  totalAmount: number;
  currency: string;
  distributionMethod: 'hours_worked' | 'equal_split' | 'position_based' | 'points';
  distributions: Array<{
    employeeId: string;
    employeeName?: string;
    amount: number;
    calculationDetails: string;
  }>;
  status: 'pending' | 'approved' | 'paid';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface CreateTipPoolInput {
  shiftId?: string;
  date: string;
  totalAmount: number;
  currency?: string;
  distributionMethod: TipPool['distributionMethod'];
  participantIds: string[];
  notes?: string;
}

export interface CompensationSettings {
  id: string;
  tenantId: string;
  previewModeEnabled: boolean;
  validationRules: {
    enabled: boolean;
    minimumWageThreshold?: number;
    maximumBonusAmount?: number;
    warnOnWeekendWork?: boolean;
    warnOnExcessiveHours?: boolean;
  };
  approvalWorkflow: {
    managerApproval: boolean;
    hrApproval: boolean;
    worksCouncilApproval: boolean;
  };
  defaultLanguage: 'en' | 'ar' | 'de';
  exportSettings: {
    includeDisclaimerFooter: boolean;
    logExports: boolean;
  };
  updatedAt?: { seconds: number; nanoseconds: number } | Date | string;
}

export interface DisclaimerStatus {
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  language?: 'en' | 'ar' | 'de';
}

// =============================================================================
// Bonus Operations
// =============================================================================

/**
 * Create a bonus payment
 */
export async function createBonusPayment(
  input: CreateBonusPaymentInput
): Promise<{ success: boolean; bonusPaymentId: string }> {
  return apiRequest(FUNCTION_NAME, '/bonuses', {
    method: 'POST',
    body: input,
  });
}

/**
 * List bonus payments
 */
export async function listBonusPayments(
  tenantId: string,
  options?: {
    employeeId?: string;
    status?: string;
    type?: string;
    limit?: number;
  }
): Promise<{ bonusPayments: BonusPayment[] }> {
  return apiRequest(FUNCTION_NAME, '/bonuses', {
    method: 'GET',
    params: { tenantId, ...options },
  });
}

/**
 * Get a specific bonus payment
 */
export async function getBonusPayment(
  id: string,
  tenantId: string
): Promise<{ bonusPayment: BonusPayment }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}`, {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Approve a bonus payment (Admin only)
 */
export async function approveBonusPayment(
  id: string,
  input: { tenantId: string; notes?: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/approve`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Reject a bonus payment (Admin only)
 */
export async function rejectBonusPayment(
  id: string,
  input: { tenantId: string; reason: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/reject`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Mark bonus payment as paid (Admin only)
 */
export async function payBonusPayment(
  id: string,
  input: { tenantId: string; payslipId?: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/pay`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Reverse a bonus payment (Admin only)
 */
export async function reverseBonusPayment(
  id: string,
  input: { tenantId: string; reason: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/reverse`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Override bonus amount (Admin only - liability-free architecture)
 */
export async function overrideBonusAmount(
  id: string,
  input: { tenantId: string; amount: number; reason: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/override`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Dispute a bonus payment
 */
export async function disputeBonusPayment(
  id: string,
  input: { tenantId: string; reason: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/dispute`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Resolve a bonus dispute (Admin only)
 */
export async function resolveDispute(
  id: string,
  input: { tenantId: string; resolution: string; finalAmount?: number }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/bonuses/${id}/resolve`, {
    method: 'POST',
    body: input,
  });
}

// =============================================================================
// Commission Plans
// =============================================================================

/**
 * Create a commission plan (Admin only)
 */
export async function createCommissionPlan(
  input: CreateCommissionPlanInput
): Promise<{ success: boolean; planId: string }> {
  return apiRequest(FUNCTION_NAME, '/commissions/plans', {
    method: 'POST',
    body: input,
  });
}

/**
 * List commission plans
 * P3-1: Server-side search support to prevent client-side OOM at 500+ records
 */
export async function listCommissionPlans(
  tenantId: string,
  options?: { status?: string; searchTerm?: string; limit?: number; offset?: number }
): Promise<{ plans: CommissionPlan[] }> {
  return apiRequest(FUNCTION_NAME, '/commissions/plans', {
    method: 'GET',
    params: { tenantId, ...options },
  });
}

/**
 * Get a specific commission plan
 */
export async function getCommissionPlan(
  id: string,
  tenantId: string
): Promise<{ plan: CommissionPlan }> {
  return apiRequest(FUNCTION_NAME, `/commissions/plans/${id}`, {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Update a commission plan (Admin only)
 */
export async function updateCommissionPlan(
  id: string,
  input: Partial<CreateCommissionPlanInput> & { tenantId: string; version: number }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/commissions/plans/${id}`, {
    method: 'PUT',
    body: input,
  });
}

/**
 * Archive a commission plan (Admin only)
 */
export async function archiveCommissionPlan(
  id: string,
  tenantId: string
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/commissions/plans/${id}`, {
    method: 'DELETE',
    params: { tenantId },
  });
}

/**
 * Calculate commission for an employee
 */
export async function calculateEmployeeCommission(input: {
  tenantId: string;
  employeeId: string;
  planId: string;
  salesAmount: number;
  periodStart: string;
  periodEnd: string;
}): Promise<{ commission: number; breakdown: unknown }> {
  return apiRequest(FUNCTION_NAME, '/commissions/calculate', {
    method: 'POST',
    body: input,
  });
}

/**
 * Batch calculate commissions for multiple employees
 */
export async function batchCalculateCommissions(input: {
  tenantId: string;
  planId: string;
  employeeIds: string[];
  periodStart: string;
  periodEnd: string;
}): Promise<{ results: Array<{ employeeId: string; commission: number }> }> {
  return apiRequest(FUNCTION_NAME, '/commissions/batch', {
    method: 'POST',
    body: input,
  });
}

// =============================================================================
// Tip Pools
// =============================================================================

/**
 * Create a tip pool (Manager/Admin)
 */
export async function createTipPool(
  input: CreateTipPoolInput
): Promise<{ success: boolean; tipPoolId: string }> {
  return apiRequest(FUNCTION_NAME, '/tip-pools', {
    method: 'POST',
    body: input,
  });
}

/**
 * List tip pools
 */
export async function listTipPools(
  tenantId: string,
  options?: { status?: string; shiftId?: string; limit?: number }
): Promise<{ tipPools: TipPool[] }> {
  return apiRequest(FUNCTION_NAME, '/tip-pools', {
    method: 'GET',
    params: { tenantId, ...options },
  });
}

/**
 * Get a specific tip pool
 */
export async function getTipPool(
  id: string,
  tenantId: string
): Promise<{ tipPool: TipPool }> {
  return apiRequest(FUNCTION_NAME, `/tip-pools/${id}`, {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Update a tip pool (Manager/Admin)
 */
export async function updateTipPool(
  id: string,
  input: { totalAmount?: number; notes?: string }
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/tip-pools/${id}`, {
    method: 'PUT',
    body: input,
  });
}

/**
 * Distribute tip pool amounts (Manager/Admin)
 */
export async function distributeTipPool(
  id: string
): Promise<{ success: boolean; distributions: TipPool['distributions'] }> {
  return apiRequest(FUNCTION_NAME, `/tip-pools/${id}/distribute`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Approve tip pool (Admin only)
 */
export async function approveTipPool(
  id: string
): Promise<{ success: boolean; bonusPaymentIds: string[] }> {
  return apiRequest(FUNCTION_NAME, `/tip-pools/${id}/approve`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Mark tip pool as paid (Admin only)
 */
export async function markTipPoolPaid(
  id: string
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, `/tip-pools/${id}/mark-paid`, {
    method: 'POST',
    body: {},
  });
}

// =============================================================================
// Salary Management
// =============================================================================

/**
 * Get employee compensation profile
 */
export async function getCompensation(
  tenantId: string,
  employeeId: string
): Promise<{ compensation: CompensationProfile | null }> {
  return apiRequest(FUNCTION_NAME, `/salary/${employeeId}`, {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Update employee compensation (Admin only)
 */
export async function updateCompensation(
  employeeId: string,
  input: UpdateCompensationPayload
): Promise<{ success: boolean; compensation: CompensationProfile['baseSalary'] }> {
  return apiRequest(FUNCTION_NAME, `/salary/${employeeId}`, {
    method: 'PUT',
    body: input,
  });
}

// =============================================================================
// Settings & Disclaimer
// =============================================================================

/**
 * Get compensation settings for tenant
 */
export async function getCompensationSettings(
  tenantId: string
): Promise<{ settings: CompensationSettings }> {
  return apiRequest(FUNCTION_NAME, '/settings', {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Update compensation settings (Admin only)
 */
export async function updateCompensationSettings(
  input: { tenantId: string } & Partial<Omit<CompensationSettings, 'id' | 'tenantId'>>
): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, '/settings', {
    method: 'PUT',
    body: input,
  });
}

/**
 * Check disclaimer acknowledgment status
 */
export async function checkDisclaimerStatus(
  tenantId: string
): Promise<CheckDisclaimerStatusResponse> {
  return apiRequest(FUNCTION_NAME, '/disclaimer/status', {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Acknowledge compensation disclaimer (Admin only)
 */
export async function acknowledgeCompensationDisclaimer(input: {
  tenantId: string;
  language: 'en' | 'ar' | 'de';
}): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, '/disclaimer/acknowledge', {
    method: 'POST',
    body: input,
  });
}

/**
 * Check payroll export disclaimer acknowledgment status
 */
export async function checkPayrollDisclaimerStatus(
  tenantId: string
): Promise<CheckDisclaimerStatusResponse> {
  return apiRequest(FUNCTION_NAME, '/disclaimer/payroll-status', {
    method: 'GET',
    params: { tenantId },
  });
}

/**
 * Acknowledge payroll export disclaimer (Admin only)
 *
 * Records that the administrator has read and acknowledged that EtchAre is a
 * data export tool only — NOT a payroll processor — and is NOT affiliated
 * with DATEV eG.
 */
export async function acknowledgePayrollDisclaimer(input: {
  tenantId: string;
  language: 'en' | 'ar' | 'de';
}): Promise<{ success: boolean }> {
  return apiRequest(FUNCTION_NAME, '/disclaimer/acknowledge-payroll', {
    method: 'POST',
    body: input,
  });
}

// =============================================================================
// Analytics & Reports
// =============================================================================

/**
 * Get compensation analytics
 */
export async function getCompensationAnalytics(
  tenantId: string,
  options?: { startDate?: string; endDate?: string; departmentId?: string }
): Promise<{ analytics: unknown }> {
  return apiRequest(FUNCTION_NAME, '/analytics', {
    method: 'GET',
    params: { tenantId, ...options },
  });
}

/**
 * Export compensation analytics
 */
export async function exportCompensationAnalytics(input: {
  tenantId: string;
  startDate: string;
  endDate: string;
  format?: 'csv' | 'xlsx';
}): Promise<{ exportUrl: string }> {
  return apiRequest(FUNCTION_NAME, '/analytics/export', {
    method: 'POST',
    body: input,
  });
}

/**
 * Generate compensation report
 */
export async function generateCompensationReport(input: {
  tenantId: string;
  reportType: 'summary' | 'detailed' | 'department';
  startDate: string;
  endDate: string;
  departmentId?: string;
}): Promise<{ report: unknown }> {
  return apiRequest(FUNCTION_NAME, '/reports/generate', {
    method: 'POST',
    body: input,
  });
}

/**
 * Generate payroll snapshot
 */
export async function generatePayrollSnapshot(input: {
  format: 'csv' | 'json' | 'datev' | 'excel';
  month: number;
  year: number;
  includeBonus?: boolean;
}): Promise<{ format: string; data: string; filename: string }> {
  return apiRequest(FUNCTION_NAME, '/payroll/snapshot', {
    method: 'POST',
    body: input,
  });
}

/**
 * Get payroll summary
 */
export async function getPayrollSummary(options?: {
  month?: number;
  year?: number;
}): Promise<PayrollSummary> {
  return apiRequest<PayrollSummary>(FUNCTION_NAME, '/payroll/summary', {
    method: 'GET',
    params: options,
  });
}

// =============================================================================
// Employee API (used by compensation module)
// =============================================================================

/**
 * List employees for dropdown/selection UI
 * Returns lightweight employee summaries (id, name, email, role, position)
 * Replaces: listEmployees httpsCallable
 */
export async function listEmployees(): Promise<{
  success: boolean;
  employees: Array<{
    id: string;
    name: string;
    email?: string;
    role?: string;
    position?: string;
  }>;
}> {
  const EMPLOYEE_API = 'employeeApi';
  return apiRequest(EMPLOYEE_API, '/', {
    method: 'GET',
  });
}
